import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { archiveSummaryQuery, getHealth, type LastImport } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatDateTime } from "@/lib/format";

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn("inline-block size-2 rounded-full", ok ? "bg-primary" : "bg-danger")}
    />
  );
}

function LastImportValue({ batch }: { batch: LastImport | null }) {
  const { t } = useTranslation();
  if (!batch) return <dd className="text-ink-subtle">{t("dashboard.status.noImport")}</dd>;
  return (
    <dd>
      <span className="flex flex-wrap items-center gap-x-2">
        <StatusDot ok={batch.status === "succeeded"} />
        <span className="font-medium">{t(`dashboard.status.importOutcome.${batch.status}`)}</span>
        {batch.finished_at && (
          <span className="text-ink-subtle tabular-nums">{formatDateTime(batch.finished_at)}</span>
        )}
      </span>
      <span className="block text-ink-subtle">
        {t("dashboard.status.importNewTickets", { count: batch.tickets_new })}
      </span>
    </dd>
  );
}

export function SystemStatusCard() {
  const { t } = useTranslation();
  const health = useQuery({ queryKey: ["health"], queryFn: getHealth, refetchInterval: 30_000 });
  const summary = useQuery(archiveSummaryQuery);

  return (
    <Card aria-labelledby="system-status-title">
      <CardHeader title={<span id="system-status-title">{t("dashboard.status.title")}</span>} />
      <CardBody>
        {health.isPending && <p className="text-sm text-ink-muted">{t("dashboard.status.loading")}</p>}
        {health.isError && (
          <p role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
            {t("dashboard.status.error")}
          </p>
        )}
        {health.data && (
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-3 text-sm">
            <dt className="text-ink-muted">{t("dashboard.status.version")}</dt>
            <dd className="font-medium tabular-nums">{health.data.version}</dd>
            <dt className="text-ink-muted">{t("dashboard.status.database")}</dt>
            <dd className="flex flex-wrap items-center gap-x-2">
              <StatusDot ok={health.data.database.ok} />
              <span className="font-medium">
                {health.data.database.ok
                  ? t("dashboard.status.connected")
                  : health.data.database.problem
                    ? t(`service.${health.data.database.problem}.title`, {
                        defaultValue: t("dashboard.status.unreachable"),
                      })
                    : t("dashboard.status.unreachable")}
              </span>
              {health.data.database.version && (
                <span className="text-ink-subtle tabular-nums">
                  MariaDB {health.data.database.version.replace(/-MariaDB.*$/i, "")}
                </span>
              )}
            </dd>
            {summary.data && (
              <>
                <dt className="text-ink-muted">{t("dashboard.status.lastImport")}</dt>
                <LastImportValue batch={summary.data.last_import} />
              </>
            )}
          </dl>
        )}
      </CardBody>
    </Card>
  );
}
